import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Alert, Stack, Typography } from '@mui/material';
import { CellSelectionOptions, CellValueChangedEvent, GetRowIdParams } from 'ag-grid-community';
import { memo } from 'react';
import { AgTableWithBaseStyle } from '../../../components/AgTable/AgTable2';
import { FormLoadingButton } from '../../../components/Form/FormComponents';
import { Loader } from '../../../components/Loader/Loader';
import { NavigationBlocker } from '../../CompanyProfiles/Scenarios/SingleScenario/block-navigation/NavigationBlocker';
import { NoDataMessage2 } from '../../CompanyProfiles/components/Messages/HelpMessage';
import { usePortCos } from '../../Finance2/hooks/usePortCos';
import { InfoBox } from '../../Mappings/forms/InfoBox';
import {
  autoGroupColumnDef,
  defaultKpiCompanySettingsColDef,
  KpiCompanySettingsGridStyles,
  useKpiCollectionCompanySettingsColDefs,
  useKpiCompanySettingsGridData,
} from './hooks/kpiCompanySettingsGridData';
import { useBulkSaveKpiCompanySettingsGridData } from './hooks/useBulkSaveKpiCompanySettingsGridData';
import { useUnsavedChangesNavigationBlocker } from './hooks/useUnsavedChangesNavigationBlocker';
import { KpiCompanySettingsGridRow } from '../../../schemas/KpiCollectionCompanySettings.schema';
import {
  useKpiCollectionCompanySettingsState,
  useKpiCollectionSettingsState,
} from './state/kpi-collection-state';

export function KpiCompanySettings() {
  // invalidate portcos on mount
  const { loading: isPortcoLoading, error: errorPortco, data: portcos } = usePortCos();
  const {
    data: kpiCollectionSettings,
    isLoading: isLoadingKpiCollectionSettings,
    error: errorKpiCollectionSettings,
  } = useKpiCollectionSettingsState();
  const {
    data: kpiCollectionCompanySettings,
    isLoading: isLoadingKpiCollectionCompanySettings,
    error: errorKpiCollectionCompanySettings,
  } = useKpiCollectionCompanySettingsState();
  if (errorKpiCollectionSettings || errorKpiCollectionCompanySettings || errorPortco) {
    return (
      <Alert severity='error' sx={{ width: '100%', height: 'unset' }}>
        Error loading data.
      </Alert>
    );
  }
  if (isLoadingKpiCollectionSettings || isLoadingKpiCollectionCompanySettings || isPortcoLoading) {
    return <Loader />;
  }

  if (!kpiCollectionSettings || !kpiCollectionCompanySettings || !portcos) {
    return <NoDataMessage2 />;
  }
  return <KpiCompanySettingsGrid />;
}

export function KpiCompanySettingsGrid() {
  const { onCellValueChanged, onSave, hasUnsavedChanges, isSaving } = useBulkSaveKpiCompanySettingsGridData();
  const { shouldBlock, doBeforeProceed } = useUnsavedChangesNavigationBlocker({
    hasUnsavedChanges,
    onSave,
    isSaving,
  });

  return (
    <Stack height='100%' width='100%' maxWidth={'100%'} p='1rem' gap='0.5rem'>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <KpiCompanySettingsTitle />
        <FormLoadingButton
          onClick={onSave}
          loading={isSaving}
          disabled={!hasUnsavedChanges || isSaving}
          size='medium'
        >
          Save Changes
        </FormLoadingButton>
      </Stack>
      <KpiCompanySettingsGridTable onCellValueChanged={onCellValueChanged} />
      <NavigationBlocker
        shouldBlock={shouldBlock}
        title={'Do you want to save changes before leaving?'}
        message={<InfoBox message={'You have unsaved changes'} />}
        doBeforeProceed={doBeforeProceed}
        proceedProps={{ label: 'Leave Without Saving' }}
      />
    </Stack>
  );
}

const KpiCompanySettingsGridTable = memo(function KpiCompanySettingsGridTable({
  onCellValueChanged,
}: {
  onCellValueChanged: (event: CellValueChangedEvent<KpiCompanySettingsGridRow>) => void;
}) {
  const rowData = useKpiCompanySettingsGridData();
  const columnDefs = useKpiCollectionCompanySettingsColDefs();

  return (
    <Stack height='100%' width='100%' maxWidth='100%' css={KpiCompanySettingsGridStyles}>
      <AgTableWithBaseStyle
        columnDefs={columnDefs}
        rowData={rowData}
        defaultColDef={defaultKpiCompanySettingsColDef}
        onCellValueChanged={onCellValueChanged}
        getRowId={getKpiCompanySettingsRowId}
        cellSelection={cellSelection}
        suppressLastEmptyLineOnPaste
        groupDefaultExpanded={-1}
        autoGroupColumnDef={autoGroupColumnDef}
        suppressAggFuncInHeader={true}
      />
    </Stack>
  );
});

const cellSelection: CellSelectionOptions = {
  handle: {
    mode: 'fill',
    direction: 'y', // Optional: restrict to vertical only (single column)
    suppressClearOnFillReduction: true, // Optional: prevent clearing when reducing range
  },
};

function getKpiCompanySettingsRowId(params: GetRowIdParams) {
  return String(params.data?.companyId ?? '');
}

export function KpiCompanySettingsTitle() {
  return (
    <div>
      <Typography variant='h5' mb='0.2rem'>
        Companies
      </Typography>
      <Typography
        variant='body2'
        color='text.secondary'
        display={'flex'}
        alignItems={'center'}
        gap={1}
        mb={'0.25rem'}
      >
        <InfoOutlinedIcon fontSize='small' />
        Double-click cells to edit, or copy-paste data into the grid.
      </Typography>
    </div>
  );
}
